import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "@/auth/SessionProvider";
import { CrmLeadModuleFieldsPanel } from "@/components/crm/CrmLeadModuleFieldsPanel";
import {
  DEFAULT_CRM_LEAD_ENTRY,
  fetchCrmLeadModuleSettings,
  saveCrmLeadModuleDefaultEntry,
  type CrmDefaultLeadEntry,
} from "@/lib/crmLeadModuleSettings";
import {
  fetchCrmLeadModuleFields,
  saveCrmLeadModuleFields,
  seedStandardCrmLeadModuleFields,
  type CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFieldsApi";
import { ArrowLeft } from "lucide-react";

const MODULE_TABS = [
  { id: "setup", label: "Setup" },
  { id: "fields", label: "Fields" },
] as const;

type ModuleTabId = (typeof MODULE_TABS)[number]["id"];

function normalizeTab(raw: string | null): ModuleTabId {
  const t = (raw || "").toLowerCase();
  return t === "fields" ? "fields" : "setup";
}

function canManageCrmSettings(role: string | undefined): boolean {
  return role === "super_admin" || role === "account_owner" || role === "account_admin";
}

export function CrmLeadModuleSettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const userRole = session.data?.user?.role ?? undefined;
  const canManage = canManageCrmSettings(userRole);

  const activeTab = useMemo(() => normalizeTab(searchParams.get("tab")), [searchParams]);

  const [defaultLeadEntry, setDefaultLeadEntry] = useState<CrmDefaultLeadEntry>(DEFAULT_CRM_LEAD_ENTRY);
  const [fieldSections, setFieldSections] = useState<CrmLeadModuleFieldsDocument["sections"]>(
    seedStandardCrmLeadModuleFields(),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldsSaving, setFieldsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldsMessage, setFieldsMessage] = useState<string | null>(null);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [fieldsApiLive, setFieldsApiLive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [entry, fieldsResult] = await Promise.all([
        fetchCrmLeadModuleSettings(),
        fetchCrmLeadModuleFields(),
      ]);
      if (!cancelled) {
        setDefaultLeadEntry(entry);
        setFieldSections(fieldsResult.sections);
        setFieldsApiLive(fieldsResult.apiLive);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onTabChange = (tab: ModuleTabId) => {
    void navigate(`/crm/settings/modules/lead?tab=${tab}`, { replace: true });
  };

  const onSaveFields = async () => {
    if (!canManage) return;
    setFieldsSaving(true);
    setFieldsError(null);
    setFieldsMessage(null);
    const result = await saveCrmLeadModuleFields(fieldSections);
    setFieldsSaving(false);
    if (!result.ok) {
      setFieldsApiLive(false);
      setFieldsError(
        result.error ||
          "Could not save to the API. Changes are kept locally until Amplify deploys the new /fields route.",
      );
      return;
    }
    setFieldsApiLive(true);
    if (result.sections) setFieldSections(result.sections);
    setFieldsMessage("Saved Lead field definitions for your organization.");
  };

  const onSave = async () => {
    if (!canManage) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    const result = await saveCrmLeadModuleDefaultEntry(defaultLeadEntry);
    setSaving(false);
    if (!result.ok) {
      setError(result.error || "Could not save.");
      return;
    }
    setMessage("Saved. New Lead will use this standard Lead Entry selection.");
  };

  const entryChoiceClass = (kind: CrmDefaultLeadEntry) =>
    kind === defaultLeadEntry
      ? "rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
      : "rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10";

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-4 overflow-y-auto px-4 py-5">
      <Link
        to="/crm/settings/modules"
        className="inline-flex w-fit items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Modules
      </Link>

      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Module</p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">Lead</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Setup and field layout for the Lead module.
        </p>
      </div>

      <div
        className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-white/10"
        role="tablist"
        aria-label="Lead module settings"
      >
        {MODULE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={
              activeTab === tab.id
                ? "border-b-2 border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300"
                : "px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            }
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "setup" ? (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-zinc-950">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Lead Entry</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Choose the standard selection when users open New Lead. They can still switch before saving.
          </p>
          {loading ? <p className="mt-4 text-sm text-slate-500">Loading…</p> : null}
          {!loading ? (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!canManage || saving}
                  className={entryChoiceClass("account")}
                  onClick={() => setDefaultLeadEntry("account")}
                >
                  Lead Account
                </button>
                <button
                  type="button"
                  disabled={!canManage || saving}
                  className={entryChoiceClass("contact")}
                  onClick={() => setDefaultLeadEntry("contact")}
                >
                  Lead Contact
                </button>
              </div>
              {!canManage ? (
                <p className="mt-3 text-xs text-slate-500">
                  Only account owners and admins can change this setting.
                </p>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void onSave()}
                  className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              ) : null}
            </>
          ) : null}
          {error ? (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {error}
            </div>
          ) : null}
          {message ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {message}
            </div>
          ) : null}
        </section>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-zinc-950">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Fields</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Standard Lead sections for New Lead. Open Email, Phone, or Social on Account or Contact Lead to
            manage channel types. Each organization keeps its own definitions after the first save.
          </p>
          {!fieldsApiLive ? (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Lead Fields API is not live yet on Amplify. The editor uses seeded standards locally. Deploy the
              ATW monolith route <code className="text-[11px]">/api/crm/settings/modules/lead/fields</code>{" "}
              so saves persist in DynamoDB.
            </p>
          ) : null}
          {loading ? <p className="mt-4 text-sm text-slate-500">Loading…</p> : null}
          {!loading ? (
            <>
              <div className="mt-4">
                <CrmLeadModuleFieldsPanel
                  sections={fieldSections}
                  onChange={setFieldSections}
                  canManage={canManage}
                  disabled={fieldsSaving}
                />
              </div>
              {canManage ? (
                <button
                  type="button"
                  disabled={fieldsSaving}
                  onClick={() => void onSaveFields()}
                  className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {fieldsSaving ? "Saving…" : "Save fields"}
                </button>
              ) : null}
            </>
          ) : null}
          {fieldsError ? (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {fieldsError}
            </div>
          ) : null}
          {fieldsMessage ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {fieldsMessage}
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
